import { FunctionComponent } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar as faSolidStar } from "@fortawesome/pro-solid-svg-icons"
import { faStar as faRegularStar } from "@fortawesome/pro-regular-svg-icons"
import { useMutation } from "@apollo/client"
import {
    WELL_FAVORITES_ADD,
    WELL_FAVORITES_DELETE,
} from "../../../graphql/mutations/well_favorite"
import { useAppAlertBanner } from "../../../context/alertBannerContext"
import classNames from "classnames"
import styles from "./styles.module.scss"

interface WellFavoriteButtonProps {
    isFavorite: boolean | undefined
    wellID: string | undefined
    wellName: string | undefined
    loading: boolean
}

/**
 * WellFavoriteCell Displays an icon to mark a well as a favorite. Clicking the icon
 * toggles its favorite status via GraphQL mutations. Errors are shown via the app alert banner
 */
const WellFavoriteToggle: FunctionComponent<WellFavoriteButtonProps> = ({
    isFavorite,
    wellID,
    wellName,
    loading,
}) => {
    // context
    const alertBannerContext = useAppAlertBanner()

    // constants
    const icon = isFavorite ? faSolidStar : faRegularStar

    // mutations
    const [addFavorite] = useMutation(WELL_FAVORITES_ADD)
    const [deleteFavorite] = useMutation(WELL_FAVORITES_DELETE)

    const handleUpdateFavoriteMutation = async () => {
        if (loading || !wellID || !wellName) {
            console.error(
                "wellID or wellName is undefined. Cannot update favorite status."
            )
            return
        }

        try {
            if (isFavorite) {
                await deleteFavorite({
                    variables: {
                        WellFavoritesDeleteInput: {
                            wellIDs: [wellID],
                        },
                    },
                    optimisticResponse: {
                        wellFavoritesDelete: {
                            wells: [
                                {
                                    __typename: "Well",
                                    id: wellID,
                                    isFavorite: false,
                                },
                            ],
                            __typename: "WellFavoritesDeletePayload",
                        },
                    },
                })
            } else {
                await addFavorite({
                    variables: {
                        WellFavoritesAddInput: {
                            wellIDs: [wellID],
                        },
                    },
                    optimisticResponse: {
                        wellFavoritesAdd: {
                            wellFavorites: [
                                {
                                    well: {
                                        __typename: "Well",
                                        id: wellID,
                                        isFavorite: true,
                                    },
                                },
                            ],
                            __typename: "WellFavoritesAddPayload",
                        },
                    },
                })
            }
        } catch (error) {
            console.error("mutation error", error)
            alertBannerContext.showAppAlert({
                message: (
                    <div>
                        Failed to set <b>{wellName}</b> as a favorite.
                    </div>
                ),
                alertType: "danger",
                customAlertClass: styles.alert,
            })
        }
    }

    return (
        <div
            className={classNames(styles.star, {
                [styles.gold]: isFavorite,
                [styles.gray]: !isFavorite,
                [styles.disabled]: loading,
            })}
            onClick={loading ? undefined : handleUpdateFavoriteMutation}
            title={isFavorite ? "Unfavorite well" : "Mark well as favorite"}
        >
            <FontAwesomeIcon icon={icon} />
        </div>
    )
}

export default WellFavoriteToggle
